/**
 * Task Management Tab
 * CRM Task management interface with calendar view, list view, and lead assignment
 * Phase 7 Implementation - Connected to Real Backend API
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Tooltip,
  Alert,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  CalendarMonth as CalendarIcon,
  ViewList as ListIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as MeetingIcon,
  Assignment as TaskIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import LeadAssignmentPanel from '../components/LeadAssignmentPanel';
import { useAuthStore } from '../../../stores/authStore';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { MobileDataList } from '../../../components/mobile';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isToday, isTomorrow, isPast, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';

// Import real API hooks
import {
  useTasks,
  useTasksSummary,
  useTaskAssignees,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useUpdateTaskStatus,
} from '../hooks/useTasks';
import { CRMTask as ApiCRMTask, TaskFilters as ApiTaskFilters, CreateTaskInput } from '../api/taskApi';

// Types - Map API types to UI types
interface CRMTask {
  id: string;
  taskNo?: string;
  title: string;
  description?: string;
  type: 'CALL' | 'EMAIL' | 'MEETING' | 'FOLLOW_UP' | 'QUOTE' | 'DEMO' | 'OTHER';
  priority: 'HIGH' | 'MEDIUM' | 'LOW' | 'URGENT';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'OVERDUE';
  dueDate: string;
  completedAt?: string;
  assignedTo?: {
    id: string;
    name: string;
    email: string;
  };
  lead?: {
    id: string;
    leadNo?: string;
    contactName: string;
    companyName?: string;
  };
  customer?: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface TaskFilters {
  status?: string;
  priority?: string;
  taskType?: string;
  assignedToUserId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

interface TaskFormData {
  title: string;
  description: string;
  taskType: string;
  priority: string;
  dueDate: Date | null;
  assignedToUserId: string;
  leadId?: string;
  customerId?: string;
}

// Map API task to UI task format
const mapApiTaskToUI = (apiTask: ApiCRMTask): CRMTask => ({
  id: apiTask.id,
  taskNo: apiTask.taskNo,
  title: apiTask.title,
  description: apiTask.description,
  type: apiTask.taskType as CRMTask['type'],
  priority: apiTask.priority as CRMTask['priority'],
  status: apiTask.status as CRMTask['status'],
  dueDate: apiTask.dueDate,
  completedAt: apiTask.completedAt,
  assignedTo: apiTask.assignedTo,
  lead: apiTask.lead,
  customer: apiTask.customer,
  createdAt: apiTask.createdAt,
  updatedAt: apiTask.updatedAt,
});

// Helper functions
const getTypeIcon = (type: string) => {
  switch (type) {
    case 'CALL': return <PhoneIcon fontSize="small" />;
    case 'EMAIL': return <EmailIcon fontSize="small" />;
    case 'MEETING': return <MeetingIcon fontSize="small" />;
    case 'FOLLOW_UP': return <ScheduleIcon fontSize="small" />;
    default: return <TaskIcon fontSize="small" />;
  }
};

const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' => {
  switch (priority) {
    case 'HIGH': return 'error';
    case 'MEDIUM': return 'warning';
    default: return 'info';
  }
};

const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' => {
  switch (status) {
    case 'PENDING': return 'default';
    case 'IN_PROGRESS': return 'primary';
    case 'COMPLETED': return 'success';
    case 'CANCELLED': return 'warning';
    default: return 'default';
  }
};

const formatDueDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'MMM d, yyyy');
};

const TaskManagementTab: React.FC = () => {
  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Current user
  const currentUser = useAuthStore((state) => state.user);

  // Main Tab State
  const [mainTab, setMainTab] = useState(0);

  // State
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [myTasksOnly, setMyTasksOnly] = useState(true);
  const [filters, setFilters] = useState<TaskFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<CRMTask | null>(null);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Form state
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    taskType: 'CALL',
    priority: 'MEDIUM',
    dueDate: new Date(),
    assignedToUserId: '',
  });

  // Build API filters
  const apiFilters: ApiTaskFilters = {
    ...filters,
    search: searchQuery || undefined,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    assignedToUserId: myTasksOnly && currentUser?.id ? currentUser.id : filters.assignedToUserId,
  };

  // Real API Queries
  const { data: tasksResponse, isLoading, isError, error } = useTasks(apiFilters);
  const { data: summaryData } = useTasksSummary();
  const { data: assignees } = useTaskAssignees();

  // Real API Mutations
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const updateStatusMutation = useUpdateTaskStatus();
  const deleteTaskMutation = useDeleteTask();

  // Handlers
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      taskType: 'CALL',
      priority: 'MEDIUM',
      dueDate: new Date(),
      assignedToUserId: '',
    });
    setSelectedTask(null);
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    resetForm();
    setTaskDialogOpen(true);
  };

  const handleEditTask = (task: CRMTask) => {
    setSelectedTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      taskType: task.type,
      priority: task.priority,
      dueDate: new Date(task.dueDate),
      assignedToUserId: task.assignedTo?.id || '',
      leadId: task.lead?.id,
      customerId: task.customer?.id,
    });
    setTaskDialogOpen(true);
  };

  const handleSubmitTask = async () => {
    // Validate required fields
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    if (!formData.assignedToUserId) {
      alert('Please select an assignee');
      return;
    }
    if (!formData.dueDate) {
      alert('Due date is required');
      return;
    }

    const taskInput: CreateTaskInput = {
      title: formData.title.trim(),
      description: formData.description?.trim() || undefined,
      taskType: formData.taskType,
      priority: formData.priority,
      dueDate: formData.dueDate.toISOString(),
      assignedToUserId: formData.assignedToUserId,
      leadId: formData.leadId || undefined,
      customerId: formData.customerId || undefined,
    };

    try {
      if (selectedTask) {
        // Update existing task
        await updateTaskMutation.mutateAsync({
          id: selectedTask.id,
          ...taskInput,
        });
      } else {
        // Create new task
        await createTaskMutation.mutateAsync(taskInput);
      }

      setTaskDialogOpen(false);
      resetForm();
    } catch (error: any) {
      console.error('Failed to save task:', error);
      alert(`Failed to save task: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleToggleComplete = (task: CRMTask) => {
    const newStatus = task.status === 'COMPLETED' ? 'PENDING' : 'COMPLETED';
    updateStatusMutation.mutate({ id: task.id, status: newStatus });
  };

  const handleDeleteTask = (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      deleteTaskMutation.mutate(taskId);
    }
  };

  // Map API response to UI tasks
  const rawTasks = tasksResponse?.data || [];
  const tasks: CRMTask[] = rawTasks.map(mapApiTaskToUI);
  const totalTasks = tasksResponse?.pagination?.total || 0;

  // Use summary data from API if available, otherwise calculate from current page
  const pendingCount = summaryData?.byStatus?.pending ?? tasks.filter(t => t.status === 'PENDING').length;
  const inProgressCount = summaryData?.byStatus?.inProgress ?? tasks.filter(t => t.status === 'IN_PROGRESS').length;
  const completedCount = summaryData?.byStatus?.completed ?? tasks.filter(t => t.status === 'COMPLETED').length;
  const overdueCount = summaryData?.byStatus?.overdue ?? tasks.filter(t => t.status !== 'COMPLETED' && isPast(new Date(t.dueDate))).length;

  // Calendar view data
  const weekStart = startOfWeek(calendarDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(calendarDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const getTasksForDay = (date: Date) => {
    return tasks.filter(task => isSameDay(new Date(task.dueDate), date));
  };

  // Summary cards
  const renderSummaryCards = () => (
    <Grid container spacing={3} mb={3}>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">{pendingCount}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'grey.200' }}>
                <UncheckedIcon color="action" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">{inProgressCount}</Typography>
                <Typography variant="body2" color="text.secondary">In Progress</Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'primary.light' }}>
                <PlayIcon color="primary" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold">{completedCount}</Typography>
                <Typography variant="body2" color="text.secondary">Completed</Typography>
              </Box>
              <Avatar sx={{ bgcolor: 'success.light' }}>
                <CheckCircleIcon color="success" />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} sm={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h4" fontWeight="bold" color={overdueCount > 0 ? 'error.main' : 'text.primary'}>
                  {overdueCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">Overdue</Typography>
              </Box>
              <Avatar sx={{ bgcolor: overdueCount > 0 ? 'error.light' : 'grey.200' }}>
                <WarningIcon color={overdueCount > 0 ? 'error' : 'action'} />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Task row component for list view
  const renderTaskRow = (task: CRMTask) => (
    <TableRow key={task.id} hover>
      <TableCell padding="checkbox">
        <IconButton size="small" onClick={() => handleToggleComplete(task)}>
          {task.status === 'COMPLETED' ? (
            <CheckCircleIcon color="success" />
          ) : (
            <UncheckedIcon color="action" />
          )}
        </IconButton>
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          {getTypeIcon(task.type)}
          <Box>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ textDecoration: task.status === 'COMPLETED' ? 'line-through' : 'none' }}
            >
              {task.title}
            </Typography>
            {task.lead && (
              <Typography variant="caption" color="text.secondary">
                Lead: {task.lead.companyName || task.lead.contactName}
              </Typography>
            )}
            {task.customer && (
              <Typography variant="caption" color="text.secondary">
                Customer: {task.customer.name}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={task.type}
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={task.priority}
          color={getPriorityColor(task.priority)}
          icon={<FlagIcon />}
        />
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          <ScheduleIcon fontSize="small" color={isPast(new Date(task.dueDate)) && task.status !== 'COMPLETED' ? 'error' : 'action'} />
          <Typography
            variant="body2"
            color={isPast(new Date(task.dueDate)) && task.status !== 'COMPLETED' ? 'error' : 'text.secondary'}
          >
            {formatDueDate(task.dueDate)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        {task.assignedTo && (
          <Tooltip title={task.assignedTo.name}>
            <Avatar sx={{ width: 28, height: 28 }}>
              {task.assignedTo.name.charAt(0)}
            </Avatar>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={task.status}
          color={getStatusColor(task.status)}
        />
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" onClick={() => handleEditTask(task)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleDeleteTask(task.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  // Calendar view
  const renderCalendarView = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {format(calendarDate, 'MMMM yyyy')}
        </Typography>
        <Box>
          <Button onClick={() => setCalendarDate(addDays(calendarDate, -7))}>Previous</Button>
          <Button onClick={() => setCalendarDate(new Date())}>Today</Button>
          <Button onClick={() => setCalendarDate(addDays(calendarDate, 7))}>Next</Button>
        </Box>
      </Box>

      <Grid container spacing={1}>
        {weekDays.map((day) => {
          const dayTasks = getTasksForDay(day);
          const isCurrentDay = isToday(day);

          return (
            <Grid item xs key={day.toISOString()}>
              <Paper
                sx={{
                  p: 1,
                  minHeight: 200,
                  bgcolor: isCurrentDay ? 'primary.50' : 'background.paper',
                  border: isCurrentDay ? 2 : 1,
                  borderColor: isCurrentDay ? 'primary.main' : 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={isCurrentDay ? 'bold' : 'normal'}
                  color={isCurrentDay ? 'primary' : 'text.secondary'}
                  mb={1}
                >
                  {format(day, 'EEE d')}
                </Typography>

                {dayTasks.map((task) => (
                  <Box
                    key={task.id}
                    sx={{
                      p: 0.5,
                      mb: 0.5,
                      borderRadius: 1,
                      bgcolor: task.status === 'COMPLETED' ? 'success.50' : 'grey.100',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.200' },
                    }}
                    onClick={() => handleEditTask(task)}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getTypeIcon(task.type)}
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ textDecoration: task.status === 'COMPLETED' ? 'line-through' : 'none' }}
                      >
                        {task.title}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // List view
  const renderListView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Task</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Due Date</TableCell>
            <TableCell>Assigned</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={8}>
                  <Skeleton height={50} />
                </TableCell>
              </TableRow>
            ))
          ) : tasks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                <TaskIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No tasks found
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                  Create your first task to get started
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateTask}>
                  Create Task
                </Button>
              </TableCell>
            </TableRow>
          ) : (
            // Server-side pagination - tasks are already paginated
            tasks.map(renderTaskRow)
          )}
        </TableBody>
      </Table>
      {totalTasks > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalTasks}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      )}
    </TableContainer>
  );

  // Mobile card configuration for tasks
  const mobileTaskCardConfig = {
    primaryField: 'title' as keyof CRMTask,
    secondaryField: 'description' as keyof CRMTask,
    statusField: 'status' as keyof CRMTask,
    statusConfig: {
      PENDING: { color: '#d9d9d9' as const, label: 'Pending' },
      IN_PROGRESS: { color: '#1890ff' as const, label: 'In Progress' },
      COMPLETED: { color: '#52c41a' as const, label: 'Completed' },
      CANCELLED: { color: '#faad14' as const, label: 'Cancelled' },
      OVERDUE: { color: '#ff4d4f' as const, label: 'Overdue' },
    },
    avatar: {
      field: 'type' as keyof CRMTask,
      type: 'text' as const,
    },
    fields: [
      { key: 'type' as keyof CRMTask, label: 'Type' },
      { key: 'priority' as keyof CRMTask, label: 'Priority', render: (value: string) => value },
      { key: 'dueDate' as keyof CRMTask, label: 'Due', render: (value: string) => formatDueDate(value) },
      { key: 'assignedTo' as keyof CRMTask, label: 'Assigned', render: (value: CRMTask['assignedTo']) => value?.name || 'Unassigned' },
    ],
    onClick: (task: CRMTask) => handleEditTask(task),
  };

  // Mobile filters configuration
  const mobileTaskFilters = [
    {
      key: 'taskType',
      placeholder: 'Type',
      options: [
        { label: 'All', value: '' },
        { label: 'Call', value: 'CALL' },
        { label: 'Email', value: 'EMAIL' },
        { label: 'Meeting', value: 'MEETING' },
        { label: 'Follow Up', value: 'FOLLOW_UP' },
        { label: 'Quote', value: 'QUOTE' },
        { label: 'Demo', value: 'DEMO' },
        { label: 'Other', value: 'OTHER' },
      ],
      value: filters.taskType || '',
      onChange: (value: string) => setFilters({ ...filters, taskType: value || undefined }),
    },
    {
      key: 'status',
      placeholder: 'Status',
      options: [
        { label: 'All', value: '' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'In Progress', value: 'IN_PROGRESS' },
        { label: 'Completed', value: 'COMPLETED' },
      ],
      value: filters.status || '',
      onChange: (value: string) => setFilters({ ...filters, status: value || undefined }),
    },
    {
      key: 'priority',
      placeholder: 'Priority',
      options: [
        { label: 'All', value: '' },
        { label: 'High', value: 'HIGH' },
        { label: 'Medium', value: 'MEDIUM' },
        { label: 'Low', value: 'LOW' },
      ],
      value: filters.priority || '',
      onChange: (value: string) => setFilters({ ...filters, priority: value || undefined }),
    },
  ];

  // Mobile View
  if (isMobile) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ pb: 2 }}>
          {/* Mobile Header */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="h6" gutterBottom>
              Task Management
            </Typography>
            {isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load tasks
              </Alert>
            )}
          </Box>

          {/* Mobile Tabs */}
          <Paper sx={{ mx: 2, mb: 2 }}>
            <Tabs
              value={mainTab}
              onChange={(_, value) => setMainTab(value)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab icon={<TaskIcon />} label="Tasks" iconPosition="start" sx={{ minHeight: 48 }} />
              <Tab icon={<PersonAddIcon />} label="Assign" iconPosition="start" sx={{ minHeight: 48 }} />
            </Tabs>
          </Paper>

          {mainTab === 0 && (
            <>
              {/* Mobile Summary Cards */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Card sx={{ textAlign: 'center', py: 1 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h5" fontWeight="bold">{pendingCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Pending</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ textAlign: 'center', py: 1 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h5" fontWeight="bold" color="primary.main">{inProgressCount}</Typography>
                        <Typography variant="caption" color="text.secondary">In Progress</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ textAlign: 'center', py: 1 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h5" fontWeight="bold" color="success.main">{completedCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card sx={{ textAlign: 'center', py: 1 }}>
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Typography variant="h5" fontWeight="bold" color={overdueCount > 0 ? 'error.main' : 'text.primary'}>
                          {overdueCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Overdue</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* My Tasks Toggle + Add Task Button */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Stack direction="row" spacing={1} mb={1} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={myTasksOnly}
                        onChange={(e) => { setMyTasksOnly(e.target.checked); setPage(0); }}
                        size="small"
                        color="primary"
                      />
                    }
                    label={<Typography variant="body2" fontWeight={500}>My Tasks Only</Typography>}
                    sx={{ flex: 1, mr: 0 }}
                  />
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateTask}
                  fullWidth
                >
                  New Task
                </Button>
              </Box>

              {/* Mobile Task List */}
              <MobileDataList<CRMTask>
                data={tasks}
                keyField="id"
                cardConfig={mobileTaskCardConfig}
                loading={isLoading}
                emptyText="No tasks found"
                emptyDescription="Create your first task to get started"
                searchPlaceholder="Search tasks..."
                onSearch={(value) => setSearchQuery(value)}
                filters={mobileTaskFilters}
                showFilters={true}
                pagination={{
                  current: page + 1,
                  pageSize: rowsPerPage,
                  total: totalTasks,
                  onChange: (newPage, pageSize) => {
                    setPage(newPage - 1);
                    setRowsPerPage(pageSize);
                  },
                }}
                swipeActions={{
                  left: {
                    icon: <DeleteIcon />,
                    color: '#ff4d4f',
                    label: 'Delete',
                    onClick: (task) => handleDeleteTask(task.id),
                  },
                  right: {
                    icon: <CheckCircleIcon />,
                    color: '#52c41a',
                    label: 'Complete',
                    onClick: (task) => handleToggleComplete(task),
                  },
                }}
              />
            </>
          )}

          {mainTab === 1 && <LeadAssignmentPanel />}

          {/* Task Dialog - Full screen on mobile */}
          <Dialog
            open={taskDialogOpen}
            onClose={() => setTaskDialogOpen(false)}
            fullScreen={true}
          >
            <DialogTitle>
              {selectedTask ? 'Edit Task' : 'Create New Task'}
            </DialogTitle>
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  fullWidth
                  required
                />
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.taskType}
                    label="Type"
                    onChange={(e) => setFormData({ ...formData, taskType: e.target.value })}
                  >
                    <MenuItem value="CALL">Call</MenuItem>
                    <MenuItem value="EMAIL">Email</MenuItem>
                    <MenuItem value="MEETING">Meeting</MenuItem>
                    <MenuItem value="FOLLOW_UP">Follow Up</MenuItem>
                    <MenuItem value="QUOTE">Quote</MenuItem>
                    <MenuItem value="DEMO">Demo</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <MenuItem value="URGENT">Urgent</MenuItem>
                    <MenuItem value="HIGH">High</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                    <MenuItem value="LOW">Low</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={formData.assignedToUserId}
                    label="Assigned To"
                    onChange={(e) => setFormData({ ...formData, assignedToUserId: e.target.value })}
                  >
                    {assignees?.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <DateTimePicker
                  label="Due Date"
                  value={formData.dueDate}
                  onChange={(date) => setFormData({ ...formData, dueDate: date })}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setTaskDialogOpen(false)} variant="outlined" fullWidth>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitTask}
                disabled={!formData.title || !formData.assignedToUserId || createTaskMutation.isPending || updateTaskMutation.isPending}
                fullWidth
              >
                {(createTaskMutation.isPending || updateTaskMutation.isPending) ? 'Saving...' : selectedTask ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </LocalizationProvider>
    );
  }

  // Desktop View
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Task Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage tasks, follow-ups, and assign leads to your team
            </Typography>
          </Box>
        </Box>

        {/* Main Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={mainTab}
            onChange={(_, value) => setMainTab(value)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab icon={<TaskIcon />} label="Tasks" iconPosition="start" />
            <Tab icon={<PersonAddIcon />} label="Lead Assignment" iconPosition="start" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {mainTab === 0 && (
          <Box>
            {/* Tasks Header Actions */}
            <Box display="flex" justifyContent="flex-end" gap={2} mb={3}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, value) => value && setViewMode(value)}
                size="small"
              >
                <ToggleButton value="list">
                  <ListIcon />
                </ToggleButton>
                <ToggleButton value="calendar">
                  <CalendarIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTask}
              >
                New Task
              </Button>
            </Box>

            {/* Error Alert */}
            {isError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                Failed to load tasks. Please make sure you are logged in.
                {error && <Typography variant="caption" display="block">{(error as Error).message}</Typography>}
              </Alert>
            )}

            {/* Summary Cards */}
            {renderSummaryCards()}

            {/* Search and Filters */}
            <Stack direction="row" gap={2} mb={3} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={myTasksOnly}
                  onChange={(e) => { setMyTasksOnly(e.target.checked); setPage(0); }}
                  size="small"
                  color="primary"
                />
              }
              label={<Typography variant="body2" fontWeight={500}>My Tasks</Typography>}
              sx={{ mr: 0, border: 1, borderColor: myTasksOnly ? 'primary.main' : 'divider', borderRadius: 2, px: 1, py: 0.25, bgcolor: myTasksOnly ? 'primary.50' : 'transparent' }}
            />
          <TextField
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.taskType || ''}
              label="Type"
              onChange={(e) => { setFilters({ ...filters, taskType: e.target.value || undefined }); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="CALL">Call</MenuItem>
              <MenuItem value="EMAIL">Email</MenuItem>
              <MenuItem value="MEETING">Meeting</MenuItem>
              <MenuItem value="FOLLOW_UP">Follow Up</MenuItem>
              <MenuItem value="QUOTE">Quote</MenuItem>
              <MenuItem value="DEMO">Demo</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority || ''}
              label="Priority"
              onChange={(e) => { setFilters({ ...filters, priority: e.target.value || undefined }); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={(e) => { setFilters({ ...filters, status: e.target.value || undefined }); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETED">Completed</MenuItem>
            </Select>
          </FormControl>
            </Stack>

            {/* View Content */}
            {viewMode === 'list' ? renderListView() : renderCalendarView()}

            {/* Task Dialog */}
            <Dialog
              open={taskDialogOpen}
              onClose={() => setTaskDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                {selectedTask ? 'Edit Task' : 'Create New Task'}
              </DialogTitle>
              <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                  <TextField
                    label="Title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={formData.taskType}
                          label="Type"
                          onChange={(e) => setFormData({ ...formData, taskType: e.target.value })}
                        >
                          <MenuItem value="CALL">Call</MenuItem>
                          <MenuItem value="EMAIL">Email</MenuItem>
                          <MenuItem value="MEETING">Meeting</MenuItem>
                          <MenuItem value="FOLLOW_UP">Follow Up</MenuItem>
                          <MenuItem value="QUOTE">Quote</MenuItem>
                          <MenuItem value="DEMO">Demo</MenuItem>
                          <MenuItem value="OTHER">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          value={formData.priority}
                          label="Priority"
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        >
                          <MenuItem value="URGENT">Urgent</MenuItem>
                          <MenuItem value="HIGH">High</MenuItem>
                          <MenuItem value="MEDIUM">Medium</MenuItem>
                          <MenuItem value="LOW">Low</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <FormControl fullWidth required>
                    <InputLabel>Assigned To</InputLabel>
                    <Select
                      value={formData.assignedToUserId}
                      label="Assigned To"
                      onChange={(e) => setFormData({ ...formData, assignedToUserId: e.target.value })}
                    >
                      {assignees?.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <DateTimePicker
                    label="Due Date"
                    value={formData.dueDate}
                    onChange={(date) => setFormData({ ...formData, dueDate: date })}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitTask}
                  disabled={!formData.title || !formData.assignedToUserId || createTaskMutation.isPending || updateTaskMutation.isPending}
                >
                  {(createTaskMutation.isPending || updateTaskMutation.isPending) ? 'Saving...' : selectedTask ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* Lead Assignment Tab */}
        {mainTab === 1 && <LeadAssignmentPanel />}
      </Box>
    </LocalizationProvider>
  );
};

export default TaskManagementTab;
